import { observable, computed, action, decorate } from "mobx";
import * as mobx from 'mobx';
import { runInAction } from "mobx";

import * as books from '../communicator/books';

class StoreDiscounts {

    constructor(storeRoot) {
        this.storeRoot = storeRoot;
        this.storeAuth = this.storeRoot.storeAuth;
        this.storeBooks = this.storeRoot.storeBooks;
        this.storeOrder = this.storeRoot.storeOrder;
        this.storeUser = this.storeRoot.storeUser;
        this.storeUi = this.storeRoot.storeUi;
    }

    async calcItemDiscount(orderId, orderItem) {
        let discountPerc = 0;

        let discountPercOverruled = 0;
        let discountPercStandard = 0;
        let discountPercReseller = 0;
        let discountPercRule = 0;

        const hasDiscountsOverruled = this.checkDiscountsOverruled(orderId);
        const hasDiscountRule = this.checkDiscountRule(orderItem);
        const isReseller = await this.checkIsReseller();
        const isDiscountAvailable = await this.checkDiscountAvailable(orderItem);

        if (isDiscountAvailable) {
            if (hasDiscountsOverruled) {
                discountPercOverruled = await this.calcDiscountPercOverruled(orderId, orderItem);
                discountPerc = discountPercOverruled;
            } else {
                discountPercStandard = await this.calcDiscountPercStandard(orderItem);
                discountPerc = discountPercStandard;

                if (isReseller) {
                    discountPercReseller = await this.calcDiscountPercReseller(orderItem);
                    if (discountPercReseller > discountPerc) {
                        discountPerc = discountPercReseller;
                    }
                }

                if (hasDiscountRule) {
                    discountPercRule = this.calcDiscountPercRule(orderItem);
                    discountPerc = discountPercRule;
                }
            }
        }

        return discountPerc;
    }

    calcDiscountPercStandard = async (orderItem) => {
        let perc = 0;
        const amount = parseInt(orderItem.amount, 10);
        const minAmount = 2;

        const userId = this.storeAuth.user.is_loggedin
            ? this.storeAuth.user.user_id
            : 0;

        const result = await books.getBookDetailsBySku(orderItem.shop, orderItem.sku);
        const book = result?.data?.[0];

        if (userId !== 0 && book) {
            const userData = await this.storeUser.refreshUserData();

            if (amount >= minAmount) {
                const discountStandardPerc = userData.discount ?? 0;
                const discountStandard3PartyPerc = userData.discount_3party ?? 0;

                perc = this.checkIsSB(book)
                    ? discountStandardPerc
                    : discountStandard3PartyPerc;
            }
        }

        return perc || 0;
    };

    calcDiscountPercReseller = async (orderItem) => {
        let perc = 0;
        const amount = parseInt(orderItem.amount, 10);
        const minAmount = this.storeOrder.settings_list["resellerMinAmount"];

        const userId = this.storeAuth.user.is_loggedin
            ? this.storeAuth.user.user_id
            : 0;

        const result = await books.getBookDetailsBySku(orderItem.shop, orderItem.sku);
        const book = result?.data?.[0];

        if (userId !== 0 && book && amount >= minAmount) {
            perc = this.getPercReseller(book);
        }

        return perc;
    };

    calcDiscountPercRule = (item) => {
        let perc = 0;
        const amount = parseInt(item.amount, 10);

        const rules = this.storeUi.userDiscounts
            .filter((rule) => this.isMatchingDiscountRule(rule, item))
            .sort((a, b) => a.minamount - b.minamount);

        let idx = -1;
        rules.forEach((rule, index) => {
            if (amount >= parseInt(rule.minamount, 10)) {
                idx = index;
            }
        });

        if (idx !== -1) {
            perc = rules[idx].discount || 0;
        }

        return perc;
    };

    calcDiscountPercOverruled = async (orderId, orderItem) => {
        const amount = parseInt(orderItem.amount, 10);

        if (!orderId || amount < 1) {
            return 0;
        }

        return await this.getPercOverruled(orderId, orderItem);
    };

    /*
     * SbC check KortingToepasbaar (discount_active) in item
     */
    checkDiscountAvailable = async (item) => {
        let discountAvailable = true;

        // SbC TEMP
        if (item.sku === '9789492665740') {
            discountAvailable = false;
        }

        return discountAvailable;
    };

    /*
     * SbC check DiscountsOverruled in Bestellingen
     * NOTE: READS STATE ONLY, NO API CALLS
     */
    checkDiscountsOverruled = (orderId) => {
        if (!orderId || orderId === 0 || orderId === '0') {
            return false;
        }

        return parseInt(
            this.storeOrder.order_settings.discounts_overruled,
            10
        ) === 1;
    };

    checkIsReseller = async () => {
        const userId = this.storeAuth.user?.user_id;

        if (!userId) {
            return false;
        }

        const userData = await this.storeUser.refreshUserData();
        if (!userData) {
            return false;
        }
        return userData.is_reseller == 1 || userData.is_reseller === 'on';
    };

    checkDiscountRule = (item) => {
        const amount = parseInt(item.amount, 10);

        const rules = this.storeUi.userDiscounts
            .filter((rule) => this.isMatchingDiscountRule(rule, item))
            .sort((a, b) => a.minamount - b.minamount);

        return rules.some(
            (rule) => amount >= parseInt(rule.minamount, 10)
        );
    };

    isMatchingDiscountRule = (rule, item) => {
        const accountId = Number(this.storeUser.user_data.user_id);
        const ruleProductId = String(rule.sku ?? '').trim();
        const productIdentifiers = [item.sku, item.id, item.book_id]
            .filter((identifier) => identifier !== undefined && identifier !== null)
            .map((identifier) => String(identifier).trim());

        return productIdentifiers.includes(ruleProductId)
            && Number(rule.account_id) === accountId
            && Number(rule.is_active) === 1;
    };

    getMinAmount = () => {
        return 2;
    };

    getMinAmountRule = () => {
        return 3;
    };

    getPercReseller = (item) => {
        return this.checkIsSB(item) ? 30 : 10;
    };

    // SbC get discount from orderline
    getPercOverruled = async (orderId, orderItem) => {
        if (!orderId || orderId === 0 || orderId === '0') {
            return 0;
        }

        const dataOrderLines = await this.storeOrder.getOrderLinesData(orderId);

        const cartLine = dataOrderLines.filter(
            (orderLine) =>
                parseInt(orderLine.book_id, 10) === parseInt(orderItem.id, 10) ||
                orderLine.book_id === orderItem.sku
        );

        return cartLine.length > 0
            ? parseInt(cartLine[0].discount, 10) || 0
            : 0;
    };

    checkIsSB = (item) => {
        return item?.sb_edition === "eigen uitgaven";
    };
}

decorate(StoreDiscounts, {
    calcItemDiscount: action
});

export default StoreDiscounts;
